import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import {Subject, Subscription} from 'rxjs';
import {Router} from '@angular/router';
import {CoreConfigService} from '@core/services/config.service';
import {AuthenticationService} from '../../../../core/service';
import {
  LoginRequest,
} from '../../../../core/requests/LoginRequest';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ToastrService} from 'ngx-toastr';
import {CustomFieldService} from '../../../../core/service/custom-field.service';
import {HttpErrorResponse} from '@angular/common/http';
import {CheckUserCustomFieldResponse} from '../../../../core/responses/CheckUserCustomFieldResponse';
import {CoreLoadingScreenService} from '@core/services/loading-screen.service';
import {UserAuthenticatedDTO} from 'app/core/requests/UserAuthenticatedDTO';
import {AuthTypeRequest} from 'app/core/requests/AuthTypeRequest';
import {CheckUserType} from 'app/core/requests/CheckUserType';
import {EndpointService} from 'app/core/service/endpoint.service';
import {emailRegex, isMobile, numericIdRegex} from 'app/core/helpers/constants';
import {environment} from '../../../../../environments/environment';
import {TwoFactorCodeRequest, TwoFactorRequest} from "../../../../core/requests/TwoFactorRequest";
import {OtpTimerService} from "../../../../core/service/otp-timer-service";
import {TwoFactorResendProvider} from "../../../../core/helpers/enums";
import {Browser} from "@capacitor/browser";
import { emailOrNumericIdValidator } from 'app/core/helpers/custom-validators';
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {ShowConsentComponent} from "../../../shared/show-consent/show-consent.component";

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [TranslatePipe],
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent implements OnInit, OnDestroy {
  //  Public
  public coreConfig: any;
  public loginForm: FormGroup;
  public checkUserForm: FormGroup;
  public loading = false;
  public error = '';
  public passwordTextType: boolean = false;
  public logo: string = null;
  private readonly ngUnsubscribe = new Subject();
  public subdomain: string;
  public companyInfo: any;
  public userSSOInfo: any = {
    isFederated: false,
    wasChecked: false
  };
  private ssoUserInfo: any;

  loggedUser: UserAuthenticatedDTO;
  showOtpCode: boolean = false;
  showOtpOptions: boolean = false;
  private timerSub: Subscription;
  timeLeft: number = 0;
  otpCountdownMinutes: string;
  otpCountdownSeconds: string;
  onlySeconds = false;

  constructor(
    private authenticationService: AuthenticationService,
    private _coreConfigService: CoreConfigService,
    private customFieldService: CustomFieldService,
    private _route: ActivatedRoute,
    private _router: Router,
    private translate: TranslatePipe,
    private _translateService: TranslateService,
    private _toastrService: ToastrService,
    private loader: CoreLoadingScreenService,
    private otpTimerService: OtpTimerService,
    private modalService: NgbModal
  ) {
    this.subdomain = this._route.snapshot.params['subdomain'];
    this.ssoUserInfo = JSON.parse(localStorage.getItem('ssoUserInfo')) ?? {};
    if (this.subdomain === 'default') {
      // no company subdomain set
      this._router.navigate(['/']);
    }

    this.authenticationService.currentUserCompanyInfo.subscribe((info) => {
      this.companyInfo = info;
      if (info) {
        this.logo = info.pictureUrl;
      }
    });

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: false,
        },
        customizer: false,
      },
    };

    this.authenticationService.currentUser.subscribe((user) => {
      if (user) {
        this.loggedUser = user;
      }
    });

    this.timerSub = this.otpTimerService.getTimeLeftObservable().subscribe(time => {
      this.timeLeft = time;
      const minutes = Math.floor(time / 60);
      const seconds = time % 60;
      this.onlySeconds = minutes === 0;

      if (this._translateService.currentLang === 'ro') {
        const minuteSuffix = minutes === 1 ? '' : 'e';
        const secondSuffix = seconds === 1 ? 'ă' : 'e';

        this.otpCountdownMinutes = `${minutes} minut${minuteSuffix}`;
        this.otpCountdownSeconds = `${seconds} secund${secondSuffix}`;
      } else {
        const minuteLabel = minutes === 1 ? 'minute' : 'minutes';
        const secondLabel = seconds === 1 ? 'second' : 'seconds';

        this.otpCountdownMinutes = `${minutes} ${minuteLabel}`;
        this.otpCountdownSeconds = `${seconds} ${secondLabel}`;
      }
    });
  }

  /**
   * On init
   */
  ngOnInit(): void {
    this.loader.hide();
    this.loginForm = new FormGroup({
      loginType: new FormControl(1, []),
      userIdentifier: new FormControl(this.ssoUserInfo.email ?? '', [Validators.required, Validators.pattern(emailRegex)]),
      password: new FormControl('', [Validators.required]),
      otpCode: new FormControl('', []),
    });

    if(this.subdomain === 'pepsico') {
      this.checkUserForm = new FormGroup({
        userIdentifier: new FormControl(this.ssoUserInfo.email ?? '', [Validators.required, emailOrNumericIdValidator(emailRegex, numericIdRegex)]),
      });
    } else {
      this.checkUserForm = new FormGroup({
        userIdentifier: new FormControl(this.ssoUserInfo.email ?? '', [Validators.required, Validators.pattern(emailRegex)]),
      });
    }

    // // Subscribe to config changes
    this._coreConfigService.config.subscribe((config) => {
      this.coreConfig = config;
    });
    this.checkSSOLoginFailed();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
    this.timerSub?.unsubscribe();
    this.otpTimerService.stopTimer();
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  get checkUserFormControls() {
    return this.checkUserForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  /**
   * Login
   */
  login() {
    this.loading = true;
    // stop here if form is invalid
    if (this.loginForm.invalid) {
      this.loading = false;
      return;
    }

    // Login
    if (this.loggedUser && this.loggedUser.hasTwoFactorAuthentication) {
      const params: TwoFactorRequest = {
        email: this.f.userIdentifier.value,
        code: this.f.otpCode.value,
        platformUrl: this.subdomain,
        temporaryToken: this.loggedUser.temporaryAuthenticationToken
      };
      this.authenticationService.verifyTwoFactorAuthentication(params).subscribe(
        (userAuth) => {
          this.onLoginSuccess(userAuth);
        },
        (error) => {
          this.onLoginFail(error, true);
        }
      );
    } else {
      const params: LoginRequest = {
        loginType: parseInt(this.f.loginType.value, 10),
        userIdentifier: this.f.userIdentifier.value,
        password: this.f.password.value,
        platformUrl: this.subdomain
      };
      this.authenticationService.login(params).subscribe(
        (userAuth) => {
          this.onLoginSuccess(userAuth);
        },
        (error) => {
          this.onLoginFail(error, false);
        }
      );
    }
  }

  public onLoginSuccess(userResponse: UserAuthenticatedDTO) {
    // 2FA step 1: backend returns hasTwoFactorAuthentication=true with no accessToken.
    // Show OTP input and return early. showOtpCode is set to true so that on step 2
    // (after OTP verify) this block is skipped and we fall through to the consent check.
    if (userResponse.hasTwoFactorAuthentication && !this.showOtpCode) {
      this.loggedUser = userResponse;
      this.loginForm.get('otpCode')?.setValidators([Validators.required]);
      this.showOtpCode = true;
      this.otpTimerService.startCountdown();
      return;
    }

    // Reached after: normal login (no 2FA), or 2FA step 2 (OTP verified).
    // Check if user needs to accept latest consents before proceeding.
    if (!userResponse.latestConsentAccepted) {
      this.requireUserAcceptance(userResponse);
      return;
    }
    this.proceedAfterConsent();
  }

  private proceedAfterConsent() {
    const userId = this.authenticationService.currentUserValue.userId;
    this.customFieldService.checkUserCustomFieldRegister(userId).subscribe(
      (data) => {
        this.onCheckUserCustomFieldRegisterSuccess(data);
      },
      (error) => {
        this.onCheckUserCustomFieldRegisterFail(error);
      }
    );
  }

  private requireUserAcceptance(userResponse: UserAuthenticatedDTO) {
    this.loading = false;
    const modalRef = this.modalService.open(ShowConsentComponent, {
      centered: true,
      backdrop: 'static',
      size: 'sm',
    });
    modalRef.componentInstance.userId = userResponse.userId;
    modalRef.componentInstance.accessToken = userResponse.accessToken;
    modalRef.componentInstance.userEmail = userResponse.email;

    modalRef.result.then(({ res, email }: { res: boolean; email: string }) => {
      if (res) {
        // Store session only after consent is accepted
        this.authenticationService.storeUser(userResponse);
        this.proceedAfterConsent();
      } else {
        // User was never stored in session, no need to logout
        this._toastrService.error(
          this.translate.transform('LOGIN.MISSING_CONSENT'),
          this.translate.transform('ERROR'),
          { toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000 }
        );
      }
    });
  }

  public onCheckUserCustomFieldRegisterSuccess(
    userHasToFillCustomFields: CheckUserCustomFieldResponse
  ) {
    const subdomain =
      this.loggedUser && this.loggedUser.companySubdomain
        ? this.loggedUser.companySubdomain
        : 'default';
    if (!userHasToFillCustomFields.responseObject) {
      setTimeout(() => {
        const companyId = this.authenticationService.currentUserValue.companyId;
        this._router.navigate([
          '/' + subdomain + '/register-custom-fields/' + companyId,
        ]);
      }, 100);
    } else {
      // redirect to home page
      setTimeout(() => {
        const returnUrl = localStorage.getItem('returnUrl') || `/${subdomain}/home`;
        localStorage.removeItem('returnUrl');
        this._router.navigate([returnUrl]);
      }, 100);
    }
  }

  public onCheckUserCustomFieldRegisterFail(error: HttpErrorResponse) {
  }

  public onLoginFail(error: any, from2Fa: boolean) {
    error.messages.forEach((errorItem) => {
      if (from2Fa) {
        this.loginForm.get('otpCode')?.setErrors({
          invalidCode: true,
          error: errorItem
        });
        if (errorItem === 'LOGIN.TEMP_AUTH_TOKEN_EXPIRED' || errorItem === 'LOGIN.INVALID_TWO_FACTOR_TOKEN') {
          this._router.navigate([`${this.subdomain}/authentication/login`]).then(() => {
            this.resetForm();

            this._toastrService.error(
              this.translate.transform(errorItem),
              this.translate.transform('ERROR'),
              {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
            );
          });
        }
      } else {
        this._toastrService.error(
          this.translate.transform(errorItem),
          this.translate.transform('ERROR'),
          {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
        );
      }
    });
  }

  /**
   * Login
   */
  checkUserAuthType() {
    this.loader.show();
    if (this.checkUserForm.invalid) {
      this.loader.hide();
      return;
    }

    // Login
    const params: AuthTypeRequest = {
      subdomain: this.subdomain,
      email: this.checkUserFormControls.userIdentifier.value,
    };
    this.authenticationService.getUserAuthType(params).subscribe(
      (response) => {
        this.onCheckUserAuthTypeSuccess(response);
      },
      (error) => {
        this.onCheckUserAuthTypeFail(error);
      }
    );
  }

  public onCheckUserAuthTypeSuccess(response: CheckUserType) {
    this.loader.show();
    this.userSSOInfo.isFederated = response.isFederated;
    this.userSSOInfo.wasChecked = true;
    if (this.userSSOInfo.isFederated) {
      setTimeout(() => {
        this.loader.show();
      }, 0);
      localStorage.setItem('ssoUserInfo', JSON.stringify({
        email: this.checkUserFormControls.userIdentifier.value,
        subDomain: this.subdomain,
        ssoLoginSuccess: true
      }));
      let redirectBase = window.location.origin;
      if (isMobile()) {
        redirectBase = environment.sso.mobileRedirectUrl;
      }
      const redirectUrl = `${redirectBase}/auth/sso`;
      const ssoUrl = EndpointService.getSSOLoginLink(
        this.subdomain,
        response.homeRealmDomainHint,
        redirectUrl,
        isMobile(),
        this.checkUserFormControls.userIdentifier.value
      );

      if (isMobile()) {
        Browser.open({
          url: ssoUrl,
          presentationStyle: 'popover'
        });
      } else {
        window.location.href = ssoUrl;
      }
    } else {
      this.loader.hide();
    }
  }

  public onCheckUserAuthTypeFail(error: any) {
    this.loader.hide();
    this.userSSOInfo.isFederated = false;
    this.userSSOInfo.wasChecked = true;
  }

  resetCompanyLS() {
    localStorage.removeItem('currentUserSubdomainMybenefits');
    this._router.navigate(['/']);
  }

  public checkSSOLoginFailed() {
    if (this.ssoUserInfo.ssoLoginSuccess === false) {
      this._toastrService.error(
        this.translate.transform(this.ssoUserInfo.reason !== null ? this.ssoUserInfo.reason : 'LOGIN.LOGIN_SSO_FAILED'),
        this.translate.transform('ERROR'),
        {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
      );
      localStorage.removeItem('ssoUserInfo');
    }
  }

  resendOtp() {
    if (this.otpTimerService.isInCooldown()) {
      return;
    }

    if (this.loggedUser.isSmsLinkActive) {
      this.showOtpOptions = true;
    } else {
      const params: TwoFactorCodeRequest = {
        platformUrl: this.subdomain,
        email: this.loggedUser.email,
        tempAuthToken: this.loggedUser.temporaryAuthenticationToken,
        resendProvider: TwoFactorResendProvider.Email
      };
      this.authenticationService.resendTwoFactorCode(params).subscribe({
          next: () => {
            this.onResendTwoFactorCodeSuccess();
          },
          error: (error) => {
            this.onResendTwoFactorCodeFail(error);
          }
        }
      );
    }
  }

  private onResendTwoFactorCodeSuccess() {
    this._toastrService.success(
      this.translate.transform('LOGIN.RESEND_OTP_CODE'),
      this.translate.transform('SUCCESS'),
      {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
    );

    this.otpTimerService.startCountdown();
  }

  private onResendTwoFactorCodeFail(error: any) {
    error.messages.forEach((errorItem: string) => {
      if (errorItem !== 'RESEND_2FA_TOKEN.TEMP_TOKEN_EXPIRED' && errorItem !== 'RESEND_2FA_TOKEN.INVALID_TEMP_TOKEN') {
        const containsSmsError = errorItem.includes("SMS_SEND")
        this._toastrService.error(
          this.translate.transform(containsSmsError ? 'RESEND_2FA_TOKEN.SMS_ERROR' : errorItem),
          this.translate.transform('ERROR'),
          {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
        );
      }
    });
  }

  protected readonly isMobile = isMobile;

  protected readonly environment = environment;

  onOtpCodeSent(error?: string) {
    if (error && (error === 'RESEND_2FA_TOKEN.TEMP_TOKEN_EXPIRED' || error === 'RESEND_2FA_TOKEN.INVALID_TEMP_TOKEN')) {
      this._router.navigate([`${this.subdomain}/authentication/login`]).then(() => {
        this.resetForm();

        this._toastrService.error(
          this.translate.transform(error),
          this.translate.transform('ERROR'),
          {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
        );
      });
    }

    if (error && error.includes("SMS_SEND")) {
      this._toastrService.error(
        this.translate.transform('RESEND_2FA_TOKEN.SMS_ERROR'),
        this.translate.transform('ERROR'),
        {toastClass: 'toast ngx-toastr', closeButton: true, timeOut: 3000}
      );
      this.otpTimerService.startCountdown();
    }

    this.showOtpOptions = false;
  }

  private resetForm() {
    this.loginForm.reset({
      loginType: 1,
      userIdentifier: '',
      password: '',
      otpCode: ''
    }, {emitEvent: true});
    const otpControl = this.loginForm.get('otpCode');
    otpControl?.clearValidators();
    otpControl?.updateValueAndValidity();
    this.showOtpCode = false;
    this.loggedUser = null;
  }
}
